import React, { useEffect, useState } from "react"
import { Card, Row, Col, Form, InputNumber, Divider, Typography, Select, Input } from "antd"
import CodeEditor from "@uiw/react-textarea-code-editor"
import Styles from "./styles.module.css"
import { NotificationEditor } from "../notificationEditor"
import { SpecialEventGameScreenBannerEditor } from "../specialEventGameScreenBannerEditor"
import { SpecialEventPresetsConfigType } from "domains/managingJson/store/types"
import { HOTKEY_VALIDATION_MESSAGE, isValidHotkeyCombo } from "domains/specialEvents/hotkey"
import {
  TARGETING_FIELD_NOTE,
  TARGETING_PLACEHOLDER,
  stringifyTargeting,
  warnOnTargeting,
} from "domains/specialEvents/targeting"
import { gameLabel } from "domains/specialEvents/timeBasedGames"

const { Title } = Typography

type OffsetUnit = "%" | "px"

type SelectStackingWrapperTopOffsetUnitProps = {
  value: OffsetUnit
  onChange: (value: OffsetUnit) => void
}

const SelectStackingWrapperTopOffsetUnit = ({ value, onChange }: SelectStackingWrapperTopOffsetUnitProps) => (
  <Select value={value} onChange={onChange} className="select-after">
    <Select.Option value="%">%</Select.Option>
    <Select.Option value="px">px</Select.Option>
  </Select>
)

type GameCardProps = {
  index: number,
  contestMode?: boolean
  presetsConfig?: SpecialEventPresetsConfigType;
}

export const GameCard = ({ index, contestMode, presetsConfig }: GameCardProps) => {
  const form = Form.useFormInstance()
  const [offsetUnit, setOffsetUnit] = useState<OffsetUnit>(
    form.getFieldValue(["notificationsPerGame", index, "stackingWrapperTopOffsetUnit"]) || "%",
  )

  useEffect(() => {
    const prevOffsetValue = form.getFieldValue(["notificationsPerGame", index, "stackingWrapperTopOffset"])
    let newOffsetValue = prevOffsetValue
    if (offsetUnit === "%" && prevOffsetValue > 100) {
      newOffsetValue = 0
    }
    form.setFieldValue(["notificationsPerGame", index, "stackingWrapperTopOffset"], newOffsetValue)
    form.setFieldValue(["notificationsPerGame", index, "stackingWrapperTopOffsetUnit"], offsetUnit)
  }, [offsetUnit])

  return (
    <Card bodyStyle={{ paddingTop: 10 }} >
      <div style={{ marginBottom: 24 }} >
        <Row >
          <Col span={20} className={Styles.titleCol}>
            <Title
              className={Styles.title}
              level={2}
            >{gameLabel(form.getFieldValue(["notificationsPerGame", index, "gameId"]))} Notifications
            </Title>
            <Divider style={{ flexGrow: 1, minWidth: "auto" }} />

          </Col>
          <Col span={4}>
            {!contestMode && (
              <Form.Item
                name={[index, "notificationsSpawnCap"]}
                label="Notification Spawn Cap (0 = unlimited)"
                tooltip="Caps the number of notifications that can be triggered in a single gaming session."
                style={{ marginBottom: 30 }}
              >
                <InputNumber min={0} step={1} style={{ width: "100%" }} />
              </Form.Item>)}
          </Col>
        </Row>
        <Row>
          <Form.Item
            name={[index, "stackingWrapperTopOffset"]}
            label="Stacking Wrapper Top Offset"
            tooltip="The distance from the top of the screen to the top of the wrapper for notifications with type 'stacking'"
          >
            <InputNumber
              max={offsetUnit === "%" ? 100 : undefined}
              min={0}
              addonAfter={
                <SelectStackingWrapperTopOffsetUnit
                  value={offsetUnit}
                  onChange={value => setOffsetUnit(value)}
                />
              }
            />
          </Form.Item>
        </Row>
        {!contestMode && (
          <Row>
            <Form.Item
              name={[index, "notificationHotkey"]}
              label="Notification Hotkey (per-game override)"
              tooltip={[
                "Hotkey that opens the active clickable notification's offer in locked-cursor games, e.g. 'Ctrl+Shift+O'.",
                "Overrides the event-level default; leave empty to fall back to it.",
              ].join(" ")}
              rules={[{
                validator: async(_, value) => {
                  if (!value || isValidHotkeyCombo(value)) return
                  throw new Error(HOTKEY_VALIDATION_MESSAGE)
                },
              }]}
            >
              <Input placeholder="Ctrl+Shift+O" allowClear style={{ width: 200 }} />
            </Form.Item>
          </Row>
        )}
        {!contestMode && (
          <Row>
            <Form.Item
              name={[index, "targeting"]}
              label="Mode / map targeting"
              tooltip={[
                "Restricts this game's notifications to a mode, map or sub-game, e.g. LEGO Fortnite.",
                "Every rule must match. Leave empty to run in all modes.",
                "source defaults to game (use launcher for League of Legends queues) and match defaults to equals.",
              ].join(" ")}
              extra={TARGETING_FIELD_NOTE}
              className={Styles.targetingItem}
              getValueProps={value => ({ value: stringifyTargeting(value) })}
              validateTrigger="onBlur"
              rules={[{
                warningOnly: true,
                validator: async(_, value) => warnOnTargeting(
                  value,
                  form.getFieldValue(["notificationsPerGame", index, "gameId"]),
                ),
              }]}
            >
              <CodeEditor
                language="JSON"
                padding={12}
                minHeight={150}
                placeholder={TARGETING_PLACEHOLDER}
                className={Styles.targetingEditor}
                style={{
                  backgroundColor: "#f5f5f5",
                  fontFamily: "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                  fontSize: 12,
                }}
              />
            </Form.Item>
          </Row>
        )}

        <NotificationEditor
          parentIndex={index}
          contestMode={contestMode}
          presetsConfig={presetsConfig}
          variant={contestMode ? "full" : "compact"}
        />
        {!contestMode && (
          <div className={Styles.bannerGrid}>
            <SpecialEventGameScreenBannerEditor parentIndex={index} bannerType="endgameBanner" title="Match End Banner" />
            <SpecialEventGameScreenBannerEditor parentIndex={index} bannerType="launchGameBanner" title="Launch Game Banner" />
          </div>
        )}
      </div>
    </Card>
  )
}
